import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import type { Recommendation, RecommendationStatus } from "@/types/artie-score";
import type { StickyNote } from "@/types/collaboration";
import { CheckCircle2, XCircle, Clock, Sparkles, Upload, FileText, Pencil, MessageSquare, Plus } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { StickyNoteComponent } from "./StickyNoteComponent";
import { apiClient } from "@/lib/api";

interface RecommendationCardProps {
  recommendation: Recommendation;
  projectId: string;
  onAccept: (recId: string) => void;
  onDecline: (recId: string) => void;
  onGoDeeper: (recId: string) => void;
  onComplete: (recId: string) => void;
  readOnly?: boolean;
  canEdit?: boolean;
  isOwner?: boolean;
  onEdit?: (recId: string) => void;
  stickyNotes?: StickyNote[];
  onAddStickyNote?: (content: string) => Promise<void>;
  onUpdateStickyNote?: (noteId: string, content: string) => Promise<void>;
  onDeleteStickyNote?: (noteId: string) => Promise<void>;
  canComment?: boolean;
  currentUserId?: string;
}

const statusConfig: Record<RecommendationStatus, {
  label: string;
  color: string;
  bgColor: string;
  icon: React.ReactNode;
}> = {
  generated: {
    label: "New",
    color: "text-primary",
    bgColor: "bg-[var(--card)] border-[var(--border)]",
    icon: <Sparkles className="w-4 h-4" />
  },
  presented: {
    label: "Review",
    color: "text-primary",
    bgColor: "bg-[var(--card)] border-[var(--border)]",
    icon: <Clock className="w-4 h-4" />
  },
  accepted: {
    label: "Accepted",
    color: "text-green-500",
    bgColor: "bg-[var(--card)] border-[var(--border)]",
    icon: <CheckCircle2 className="w-4 h-4" />
  },
  in_progress: {
    label: "In Progress",
    color: "text-yellow-500",
    bgColor: "bg-[var(--card)] border-[var(--border)]",
    icon: <Clock className="w-4 h-4 animate-pulse" />
  },
  "in-progress": {
    label: "In Progress",
    color: "text-yellow-500",
    bgColor: "bg-[var(--card)] border-[var(--border)]",
    icon: <Clock className="w-4 h-4 animate-pulse" />
  },
  done: {
    label: "Done",
    color: "text-green-500",
    bgColor: "bg-[var(--card)] border-[var(--border)]",
    icon: <CheckCircle2 className="w-4 h-4" />
  },
  completed: {
    label: "Done",
    color: "text-green-500",
    bgColor: "bg-[var(--card)] border-[var(--border)]",
    icon: <CheckCircle2 className="w-4 h-4" />
  },
  declined: {
    label: "Declined",
    color: "text-muted-foreground",
    bgColor: "bg-[var(--card)] border-[var(--border)]",
    icon: <XCircle className="w-4 h-4" />
  },
  rejected: {
    label: "Declined",
    color: "text-muted-foreground",
    bgColor: "bg-[var(--card)] border-[var(--border)]",
    icon: <XCircle className="w-4 h-4" />
  },
};

const priorityConfig = {
  1: { label: "High Priority", color: "bg-red-500/10 text-red-500 border-red-500/20" },
  2: { label: "Medium Priority", color: "bg-yellow-500/10 text-yellow-500 border-yellow-500/20" },
  3: { label: "Lower Priority", color: "bg-blue-500/10 text-blue-500 border-blue-500/20" },
};

export const RecommendationCard = ({
  recommendation,
  projectId,
  onAccept,
  onDecline,
  onGoDeeper,
  onComplete,
  readOnly = false,
  canEdit = false,
  isOwner = false,
  onEdit,
  stickyNotes = [],
  onAddStickyNote,
  onUpdateStickyNote,
  onDeleteStickyNote,
  canComment = false,
  currentUserId = "",
}: RecommendationCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState("");
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);
  const [hasBeenViewed, setHasBeenViewed] = useState(false);
  const status = recommendation.status || 'generated';

  // Debug logging
  console.log('[RecommendationCard] Props:', {
    recommendationId: recommendation.id || recommendation.what_to_improve,
    canComment,
    currentUserId,
    readOnly,
    isExpanded,
    isAddingNote,
    hasOnAddStickyNote: !!onAddStickyNote,
    isNew: recommendation.is_new
  });

  // Mark recommendation as viewed when component mounts (first time user sees it)
  useEffect(() => {
    const markAsViewed = async () => {
      if (recommendation.is_new && !hasBeenViewed) {
        try {
          const recId = recommendation.id || recommendation.what_to_improve;

          // Check if this is a V2 tactics recommendation (has tactic_id field)
          const isV2Tactic = 'tactic_id' in recommendation;

          if (isV2Tactic) {
            // Use V2 tactics mark-viewed endpoint
            await apiClient.markTacticsAsViewed(projectId, [recId]);
          } else {
            // Use legacy recommendations mark-viewed endpoint
            await apiClient.markRecommendationsAsViewed(projectId, [recId]);
          }

          setHasBeenViewed(true);
        } catch (error) {
          console.error('Failed to mark recommendation as viewed:', error);
        }
      }
    };

    markAsViewed();
  }, [recommendation.is_new, recommendation.id, recommendation.what_to_improve, projectId, hasBeenViewed]);
  const statusInfo = statusConfig[status];
  const priorityInfo = priorityConfig[recommendation.priority as 1 | 2 | 3];
  const isDeclined = status === 'rejected' || status === 'declined';
  const isDone = status === 'completed' || status === 'done';
  const canGoDeeper = status === 'accepted' || status === 'in-progress' || status === 'in_progress';
  const canComplete = status === 'accepted' || status === 'in-progress' || status === 'in_progress';

  const recommendationNotes = stickyNotes.filter(
    note => note.recommendation_id === (recommendation.id || recommendation.what_to_improve)
  );

  const handleAddNote = async () => {
    if (!onAddStickyNote || !newNoteContent.trim()) return;

    setIsSubmittingNote(true);
    try {
      await onAddStickyNote(newNoteContent);
      setNewNoteContent("");
      setIsAddingNote(false);
    } catch (error) {
      console.error("Failed to add note:", error);
    } finally {
      setIsSubmittingNote(false);
    }
  };

  return (
    <div
      className={`
        rounded-xl border-2 p-4 transition-all duration-200 flex flex-col h-full
        ${isDeclined ? 'opacity-50' : 'opacity-100'}
        ${statusInfo.bgColor}
        ${isExpanded ? 'shadow-lg z-10 relative' : 'shadow-sm hover:shadow-md'}
      `}
    >
      {/* Header */}
      <div className="flex flex-col gap-2 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className={`${priorityInfo.color} border text-[10px] h-5 px-1.5`}>
            {priorityInfo.label}
          </Badge>
          <Badge variant="outline" className={`${statusInfo.color} border text-[10px] h-5 px-1.5`}>
            <span className="flex items-center gap-1">
              {statusInfo.icon}
              {statusInfo.label}
            </span>
          </Badge>
          {/* NEW tag - only show if recommendation is new and hasn't been viewed yet */}
          {recommendation.is_new && !hasBeenViewed && (
            <Badge className="bg-blue-500 text-white border-blue-500 text-[10px] h-5 px-1.5 animate-pulse">
              NEW
            </Badge>
          )}
        </div>
        <div>
          <h3
            className={`text-sm font-semibold text-foreground leading-tight ${isExpanded ? '' : 'line-clamp-2 h-10 overflow-hidden'}`}
            title={recommendation.what_to_improve}
          >
            {recommendation.what_to_improve}
          </h3>
          <p className="text-xs text-muted-foreground mt-1 truncate">
            Related to: <span className="font-medium capitalize">{recommendation.related_metric.replace('_', ' ')}</span>
          </p>
        </div>
      </div>

      {/* Why it matters */}
      <div className="mb-3 flex-1 min-h-[60px]">
        <p className="text-xs font-medium text-foreground mb-0.5">Why it matters:</p>
        <div className={`text-xs text-muted-foreground prose prose-sm max-w-none prose-p:text-muted-foreground prose-strong:text-foreground prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 ${!isExpanded ? 'line-clamp-3 overflow-hidden' : ''}`}>
          <ReactMarkdown>{recommendation.why_it_matters}</ReactMarkdown>
        </div>
      </div>

      {/* Expandable section */}
      {isExpanded && (
        <div className="mb-3 animate-in fade-in slide-in-from-top-2 duration-200 bg-background/50 p-2 rounded-lg border border-border/50">
          <p className="text-xs font-medium text-foreground mb-1">How to improve:</p>
          <div className="text-xs text-muted-foreground prose prose-sm max-w-none prose-p:text-muted-foreground prose-strong:text-foreground prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-headings:text-foreground prose-headings:font-semibold">
            <ReactMarkdown>{recommendation.how_to_improve}</ReactMarkdown>
          </div>

          {/* Completion info */}
          {isDone && recommendation.completion_method && (
            <div className="mt-3 p-2 bg-secondary/50 rounded border">
              <p className="text-[10px] font-medium text-foreground mb-1">
                Completed via: {recommendation.completion_method === 'artifact' ? 'Artifact Upload' : 'Plan Description'}
              </p>
              {recommendation.completion_method === 'artifact' && recommendation.artifact && (
                <div className="flex flex-wrap gap-2 mt-1">
                  <a
                    href={recommendation.artifact.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] text-blue-600 hover:underline flex items-center gap-1"
                  >
                    <Upload className="w-3 h-3" />
                    {recommendation.artifact.filename}
                  </a>
                </div>
              )}
              {recommendation.completion_method === 'plan' && recommendation.plan_description && (
                <p className="text-[10px] text-muted-foreground mt-1">{recommendation.plan_description}</p>
              )}
            </div>
          )}

          {/* Token usage for in-progress */}
          {canGoDeeper && recommendation.token_usage !== undefined && (
            <div className="mt-3 p-2 bg-secondary/50 rounded border">
              <p className="text-[10px] text-muted-foreground">
                Go Deeper tokens used: <span className="font-medium">{recommendation.token_usage}</span> / 10,000
              </p>
              <div className="w-full bg-gray-200 rounded-full h-1 mt-1">
                <div
                  className="bg-blue-600 h-1 rounded-full transition-all"
                  style={{ width: `${Math.min((recommendation.token_usage / 10000) * 100, 100)}%` }}
                />
              </div>
            </div>
          )}

          {/* Sticky Notes Section */}
          {recommendationNotes.length > 0 && (
            <div className="mt-3 space-y-2">
              <p className="text-xs font-medium text-foreground flex items-center gap-1">
                <MessageSquare className="w-3 h-3" />
                Comments ({recommendationNotes.length})
              </p>
              <div className="space-y-2">
                {recommendationNotes.map((note) => (
                  <StickyNoteComponent
                    key={note.id}
                    note={note}
                    onUpdate={onUpdateStickyNote}
                    onDelete={onDeleteStickyNote}
                    canEdit={canComment}
                    currentUserId={currentUserId}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Add Note Form */}
          {isAddingNote && canComment && (
            <div className="mt-3 p-2 bg-secondary/50 rounded border space-y-2">
              <Textarea
                value={newNoteContent}
                onChange={(e) => setNewNoteContent(e.target.value)}
                placeholder="Add your comment..."
                className="min-h-[80px] text-sm"
                disabled={isSubmittingNote}
              />
              <div className="flex gap-2 justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setIsAddingNote(false);
                    setNewNoteContent("");
                  }}
                  disabled={isSubmittingNote}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleAddNote}
                  disabled={isSubmittingNote || !newNoteContent.trim()}
                >
                  Add Comment
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 flex-wrap">
        {onEdit && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onEdit(recommendation.id || recommendation.what_to_improve)}
            className="bg-secondary/50 hover:bg-secondary border-border text-foreground"
          >
            <Pencil className="w-4 h-4 mr-1" />
            Edit
          </Button>
        )}

        {/* Action buttons for users with Edit permission */}
        {canEdit && (
          <>
            {/* Initial state - Accept/Decline */}
            {(status === 'generated' || status === 'presented') && !isDeclined && (
              <>
                <Button
                  size="sm"
                  onClick={() => onAccept(recommendation.id || recommendation.what_to_improve)}
                  className="bg-green-500 hover:bg-green-600 text-white"
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  Accept
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onDecline(recommendation.id || recommendation.what_to_improve)}
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Decline
                </Button>
              </>
            )}

            {/* Accepted/In Progress - Go Deeper (Owner only) */}
            {canGoDeeper && isOwner && (
              <Button
                size="sm"
                onClick={() => onGoDeeper(recommendation.id || recommendation.what_to_improve)}
                className="bg-primary text-primary-foreground hover:bg-primary/90 w-full h-8 text-xs"
              >
                <Sparkles className="w-3 h-3 mr-1" />
                Go Deeper
              </Button>
            )}

            {/* In Progress - Mark as Done */}
            {canComplete && (
              <Button
                size="sm"
                onClick={() => onComplete(recommendation.id || recommendation.what_to_improve)}
                className="bg-green-500 hover:bg-green-600 text-white w-full h-8 text-xs"
              >
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Done
              </Button>
            )}
          </>
        )}

        {/* Add Comment Button - available for Comment and Edit permissions */}
        {canComment && !isAddingNote && isExpanded && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setIsAddingNote(true)}
            className="w-full h-8 text-xs"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add Comment
          </Button>
        )}

        {/* See More Button - always at the bottom, full width */}
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full h-7 text-xs text-muted-foreground hover:text-foreground"
        >
          {isExpanded ? 'Show Less' : 'Show Details'}
        </Button>
      </div>
    </div>
  );
};